import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import api from "../../services/api";
import LandingHeader from "../landingpage/LandingHeader";
import LandingFooter from "../landingpage/Footer";

const PAGE_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', system-ui, sans-serif; background: #fff; }

  .bp-root { min-height: 100vh; display: flex; flex-direction: column; background: #fff; }

  .bp-hero {
    width: 100%;
    background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #dbeafe 100%);
    padding: 120px 24px 64px;
  }
  .bp-hero__wrap { max-width: 820px; margin: 0 auto; }
  .bp-meta { font-size: 13px; color: #7c3aed; font-weight: 600; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
  .bp-meta__sep { color: #c4b5fd; }
  .bp-meta__date { color: #64748b; font-weight: 400; }
  .bp-title {
    font-size: clamp(28px, 5vw, 48px);
    font-weight: 800;
    color: #1a1525;
    line-height: 1.15;
    letter-spacing: -0.5px;
    margin-bottom: 20px;
  }
  .bp-resumo {
    font-size: 18px;
    color: #475569;
    line-height: 1.7;
    max-width: 680px;
  }

  .bp-cover-wrap { width: 100%; max-height: 480px; overflow: hidden; }
  .bp-cover { width: 100%; height: 480px; object-fit: cover; display: block; }

  .bp-body { flex: 1; padding: 64px 24px 80px; }
  .bp-body__wrap { max-width: 820px; margin: 0 auto; }

  .bp-content {
    font-size: 17px;
    line-height: 1.8;
    color: #374151;
  }
  .bp-content h1, .bp-content h2, .bp-content h3, .bp-content h4 {
    color: #1a1525;
    font-weight: 700;
    margin: 2em 0 0.6em;
    line-height: 1.3;
  }
  .bp-content h1 { font-size: 2em; }
  .bp-content h2 { font-size: 1.5em; }
  .bp-content h3 { font-size: 1.25em; }
  .bp-content h4 { font-size: 1.1em; }
  .bp-content p { margin-bottom: 1.25em; }
  .bp-content ul, .bp-content ol { margin: 0 0 1.25em 1.5em; }
  .bp-content li { margin-bottom: 0.4em; }
  .bp-content blockquote {
    border-left: 4px solid #7c3aed;
    margin: 1.5em 0;
    padding: 12px 24px;
    background: #f5f3ff;
    border-radius: 0 8px 8px 0;
    color: #4c1d95;
    font-style: italic;
  }
  .bp-content img { max-width: 100%; border-radius: 12px; margin: 1.5em 0; }
  .bp-content a { color: #7c3aed; text-decoration: underline; }
  .bp-content a:hover { color: #6d28d9; }
  .bp-content pre, .bp-content code {
    background: #1e1b4b;
    color: #c7d2fe;
    border-radius: 8px;
    font-family: 'Fira Code', monospace;
    font-size: 14px;
  }
  .bp-content pre { padding: 16px 20px; overflow-x: auto; margin: 1.5em 0; }
  .bp-content code { padding: 2px 6px; }
  .bp-content table { width: 100%; border-collapse: collapse; margin: 1.5em 0; }
  .bp-content th, .bp-content td { border: 1px solid #e5e7eb; padding: 10px 14px; text-align: left; }
  .bp-content th { background: #f5f3ff; font-weight: 700; color: #4c1d95; }
  .bp-content tr:nth-child(even) td { background: #fafafa; }

  .bp-back {
    display: inline-flex; align-items: center; gap: 6px;
    color: #7c3aed; font-size: 14px; font-weight: 600;
    background: none; border: none; cursor: pointer;
    padding: 0; margin-bottom: 40px;
    font-family: inherit;
    transition: opacity .2s;
  }
  .bp-back:hover { opacity: .7; }

  .bp-not-found {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 80px 24px; text-align: center; gap: 16px;
  }
  .bp-not-found h2 { font-size: 28px; font-weight: 700; color: #1a1525; }
  .bp-not-found p { color: #64748b; font-size: 16px; }

  .bp-loading {
    flex: 1; display: flex; align-items: center; justify-content: center; padding: 80px;
  }
  .bp-spinner {
    width: 40px; height: 40px; border: 3px solid #ede9fe;
    border-top-color: #7c3aed; border-radius: 50%;
    animation: bp-spin 0.8s linear infinite;
  }
  @keyframes bp-spin { to { transform: rotate(360deg); } }

  @media (max-width: 768px) {
    .bp-hero { padding: 100px 20px 48px; }
    .bp-title { font-size: 28px; }
    .bp-resumo { font-size: 16px; }
    .bp-cover { height: 220px; }
    .bp-body { padding: 40px 20px 60px; }
    .bp-content { font-size: 16px; }
  }
`;

const BlogPost = () => {
  const { slug } = useParams();
  const history = useHistory();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/open/blog-posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const coverSrc = post?.imagemCapa
    ? `${backendUrl}/public/${post.imagemCapa}`
    : null;

  const handleCoverError = (e) => {
    const cur = e.currentTarget.src;
    const fallback = cur.replace("/blog/", "/");
    if (cur !== fallback) {
      e.currentTarget.src = fallback;
    } else {
      e.currentTarget.style.display = "none";
    }
  };

  const formattedDate = post?.createdAt
    ? new Date(post.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
    : "";

  return (
    <>
      <style>{PAGE_CSS}</style>
      <div className="bp-root">
        <LandingHeader scrollTo={() => { history.push("/"); }} />

        {loading && (
          <div className="bp-loading">
            <div className="bp-spinner" />
          </div>
        )}

        {!loading && notFound && (
          <div className="bp-not-found">
            <h2>Post não encontrado</h2>
            <p>O artigo que você está procurando não existe ou foi removido.</p>
            <button className="bp-back" onClick={() => history.push("/")}>
              ← Voltar para o início
            </button>
          </div>
        )}

        {!loading && post && (
          <>
            <section className="bp-hero">
              <div className="bp-hero__wrap">
                <div className="bp-meta">
                  <span>Blog</span>
                  <span className="bp-meta__sep">•</span>
                  <span className="bp-meta__date">{formattedDate}</span>
                </div>
                <h1 className="bp-title">{post.titulo}</h1>
                {post.resumo && <p className="bp-resumo">{post.resumo}</p>}
              </div>
            </section>

            {coverSrc && (
              <div className="bp-cover-wrap">
                <img
                  src={coverSrc}
                  alt={post.titulo}
                  className="bp-cover"
                  onError={handleCoverError}
                />
              </div>
            )}

            <div className="bp-body">
              <div className="bp-body__wrap">
                <button className="bp-back" onClick={() => history.goBack()}>
                  ← Voltar
                </button>
                <div
                  className="bp-content ql-editor"
                  dangerouslySetInnerHTML={{ __html: post.conteudo || "" }}
                />
              </div>
            </div>
          </>
        )}

        <LandingFooter />
      </div>
    </>
  );
};

export default BlogPost;
